import { Box, Divider } from '@material-ui/core';
import { COLORS_GRAPHS } from 'components/Charts/Colors';
import { calculPourcentage, cumulData } from 'core/utils/utils';
import React, { useState } from 'react';
import { Cell, Legend, Pie, PieChart, Tooltip } from 'recharts';

// https://stackblitz.com/edit/rechart-pie-demo?file=src%2Fcommon%2FRadianTooltip.js
// https://www.olioapps.com/blog/graph-hacking/

type Metric = 'pourcentage' | 'value';

type Props = {
    data?: any;
    metric?: Metric;
    colors?: any;
};

const SGSPieChart: React.FC<Props> = ({ data, colors = COLORS_GRAPHS(), metric = false }) => {
    const [activeIndex, setActiveIndex] = useState(0);

    const keys = Object.keys(colors);

    const onPieEnter = (_, index) => {
        setActiveIndex(index);
    };
    const total = data.length ? data.map((e) => e.value).reduce(cumulData) : 0;

    const RadianTooltip = (props) => {
        const RADIAN = Math.PI / 180;
        const { cx, cy, midAngle, outerRadius, fill, percent, value, name } = props;

        const sin = Math.sin(-RADIAN * midAngle);
        const cos = Math.cos(-RADIAN * midAngle);
        const sx = cx + (outerRadius + 10) * cos;
        const sy = cy + (outerRadius + 10) * sin;
        const mx = cx + (outerRadius + 30) * cos;
        const my = cy + (outerRadius + 30) * sin;
        const ex = mx + (cos >= 0 ? 1 : -1) * 22;
        const ey = my;
        const textAnchor = cos >= 0 ? 'start' : 'end';

        return (
            <g>
                {metric === 'pourcentage' ? (
                    <>
                        <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill="none" />
                        <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
                        <text x={ex + (cos >= 0 ? 1 : -1) * 12} y={ey} textAnchor={textAnchor} fill="#333">
                            {`${(percent * 100).toFixed(2)}%`}
                        </text>
                    </>
                ) : (
                    <>
                        <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill="none" />
                        <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
                        <text x={ex + (cos >= 0 ? 1 : -1) * 12} y={ey} textAnchor={textAnchor} fill="#333">
                            {name}
                        </text>
                        <text x={ex + (cos >= 0 ? 1 : -1) * 12} y={ey} dy={18} textAnchor={textAnchor} fill="#999">
                            {`(${value} ~ ${(percent * 100).toFixed(2)}%)`}
                        </text>
                    </>
                )}
            </g>
        );
    };
    return (
        <>
            <PieChart width={525} height={300}>
                <Pie
                    id={'stats_' + Math.random() * 100}
                    data={data}
                    activeIndex={activeIndex}
                    labelLine={true}
                    label={RadianTooltip}
                    color="#000000"
                    dataKey="value"
                    nameKey="label"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    paddingAngle={1}
                    fill="#8884d8"
                    isAnimationActive={false}
                    onMouseEnter={onPieEnter}
                >
                    {data.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={colors[keys[index]]} />
                    ))}
                </Pie>
                <Tooltip
                    content={<CustomTooltip active={undefined} payload={undefined} total={total} metric={metric} />}
                />
                <Legend />
            </PieChart>
        </>
    );
};

export default SGSPieChart;

const CustomTooltip = ({ active, payload, total, metric }) => {
    if (active) {
        const pourcentage = calculPourcentage(total, payload[0].value);
        return (
            <Box
                className="custom-tooltip"
                style={{
                    backgroundColor: '#626466',
                    color: 'white',
                    padding: '5px',
                    border: '1px solid #efefef !important',
                    borderRadius: '5px',
                }}
            >
                {metric === 'pourcentage' ? (
                    <>
                        <Box className="text-center">
                            {`${payload[0].name}`} ~ {pourcentage}%
                        </Box>
                    </>
                ) : (
                    <>
                        <Box className="text-center">{`${payload[0].name}`}</Box>
                        <Divider style={{ display: 'none' }} />
                        <Box className="text-center">
                            {`${payload[0].value}`} ~ {pourcentage}%
                        </Box>
                    </>
                )}
            </Box>
        );
    }
    return null;
};
